import { Button, cx, DialogContent, Dialogß } from '@otter-ai/den-components';
import { CopyIcon, Trash_2Icon } from '@otter-ai/den-icons';
import type * as recall from '@otter-ai/recall-client';
import { format } from 'date-fns';
import { useState } from 'react';
import { PaywallTooltip } from './PaywallTooltip';
import { TemplateDropdownOptions } from './TemplateDropdownOptions';

type TemplateTableProps = {
  templates: recall.Template[];
  isAdmin?: boolean;
  showActions?: boolean;
  atLimit?: boolean;
  onClick: (template: recall.Template) => void;
  onDelete: (template: recall.Template) => void;
  onCopy: (template: recall.Template) => void;
};

export function TemplateTable({
  templates,
  isAdmin = false,
  showActions = false,
  atLimit = false,
  onClick,
  onDelete,
  onCopy,
}: TemplateTableProps) {
  const [selectedTemplate, setSelectedTemplate] =
    useState<recall.Template | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  return (
    <>
      {showDeleteConfirmation && selectedTemplate && (
        <Dialogß
          data-testid="delete-template-dialog"
          className="w-[380px]"
          title="Delete Template?"
          onOpenChange={() => {
            setSelectedTemplate(null);
            setShowDeleteConfirmation(false);
          }}
          Footer={
            <div className="flex gap-2">
              <Button
                variant="outlineNeutral"
                version="legacy"
                onClick={() => {
                  setSelectedTemplate(null);
                  setShowDeleteConfirmation(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="negative"
                onClick={() => {
                  onDelete(selectedTemplate);
                  setSelectedTemplate(null);
                  setShowDeleteConfirmation(false);
                }}
              >
                Delete
              </Button>
            </div>
          }
        >
          <DialogContent>
            <span className="text-sm text-default font-semibold">
              Are you sure you want to delete the template{' '}
              {selectedTemplate.name}?
            </span>
          </DialogContent>
        </Dialogß>
      )}
      <table className="min-w-full table-auto">
        <thead>
          <tr className="border-b border-b-subtle">
            <th className="text-left py-3 px-4 text-sm text-subtle">
              Template
            </th>
            {isAdmin && (
              <th className="text-left py-3 px-4 text-sm text-subtle">
                Created by
              </th>
            )}
            {isAdmin && (
              <th className="text-left py-3 px-4 text-sm text-subtle">
                Last edited by
              </th>
            )}
            {showActions && <th className="py-3 px-4" />}
          </tr>
        </thead>
        <tbody className="divide-y-[1px] divide-subtle">
          {templates.map((template, index) => {
            const canClickDropdown =
              (template.permissions?.can_clone && !atLimit) ||
              template.permissions?.can_delete;

            const actionItems = [
              template.permissions?.can_delete
                ? {
                    label: 'Delete',
                    icon: <Trash_2Icon />,
                    action: () => {
                      setSelectedTemplate(template);
                      setShowDeleteConfirmation(true);
                    },
                    className: 'text-negative',
                  }
                : null,
              template.permissions?.can_clone && !atLimit
                ? {
                    label: 'Duplicate',
                    icon: <CopyIcon />,
                    action: () => onCopy(template),
                  }
                : null,
            ].filter(Boolean);

            const lastModifiedAt =
              template.last_modified_at &&
              format(new Date(template.last_modified_at), 'MMMM d, yyyy');

            return (
              <tr
                key={template.id}
                className={cx(
                  'hover:bg-default-hover py-6 text-default',
                  'cursor-pointer',
                )}
                onClick={() => onClick(template)}
                onKeyDown={() => {}}
              >
                <td
                  className="rounded-l-lg py-3 px-4 text-default flex flex-row gap-1"
                  {...(index === 0 && {
                    'data-tutorial': 'meeting-types-template-item',
                  })}
                >
                  {template.name}
                  {!template.permissions?.can_apply && <PaywallTooltip />}
                </td>
                {isAdmin && (
                  <td className="py-3 px-4 text-subtle">
                    {template.created_by?.name ?? 'Otter'}
                  </td>
                )}
                {isAdmin && (
                  <td className="py-3 px-4 text-subtle">
                    {template.last_modified_by?.name ?? 'Otter'}
                    {lastModifiedAt && `, ${lastModifiedAt}`}
                  </td>
                )}
                <td className="py-3 px-4 rounded-r-lg">
                  <div className="flex justify-end">
                    {canClickDropdown && (
                      <TemplateDropdownOptions
                        items={actionItems}
                        iconSize={28}
                      />
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
